package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.EventResponse
import org.example.project.ui.components.EmptyState
import org.example.project.ui.components.LoadingState
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@Composable
fun EventListView(
    isLoading: Boolean,
    events: List<EventResponse>,
    onEventClick: (String) -> Unit,
    onCreateClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    if (isLoading && events.isEmpty()) {
        LoadingState(
            message = "Wczytywanie Twoich eventów...",
            modifier = modifier.padding(16.dp)
        )
        return
    }

    if (events.isEmpty()) {
        EmptyState(
            title = "Brak zaplanowanych eventów",
            description = "Stwórz swój pierwszy plan wydarzenia, ułóż oś czasu i dodaj zabawy z katalogu lub ulubionych.",
            icon = AppIcons.Planner,
            actionLabel = "+ Utwórz pierwszy event",
            onAction = onCreateClick,
            modifier = modifier.padding(16.dp)
        )
        return
    }

    LazyColumn(
        modifier = modifier.fillMaxSize().padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        items(events, key = { it.id }) { event ->
            EventCard(
                event = event,
                onClick = { onEventClick(event.id) }
            )
        }
    }
}

@Composable
fun EventCard(
    event: EventResponse,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current

    Card(
        modifier = modifier
            .fillMaxWidth()
            .clickable { onClick() },
        shape = shapes.card,
        colors = CardDefaults.cardColors(containerColor = PlayfulColors.Surface),
        border = BorderStroke(1.dp, PlayfulColors.BorderSubtle),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Title and Version Row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = event.name,
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Bold,
                    color = PlayfulColors.TextPrimary,
                    modifier = Modifier.weight(1f).padding(end = 8.dp)
                )
                Surface(
                    color = PlayfulColors.CoralLight,
                    shape = shapes.pill,
                    border = BorderStroke(1.dp, PlayfulColors.CoralBorder)
                ) {
                    Text(
                        text = "w.${event.version}",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = PlayfulColors.CoralDark,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Time with Clock Icon
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = AppIcons.Clock,
                    contentDescription = null,
                    tint = PlayfulColors.Teal,
                    modifier = Modifier.size(15.dp)
                )
                Spacer(modifier = Modifier.width(6.dp))
                Text(
                    text = "${event.startTime} – ${event.endTime}",
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                    color = PlayfulColors.TextSecondary
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Block Count Badge
            Surface(
                color = PlayfulColors.TealLight,
                shape = shapes.pill,
                border = BorderStroke(1.dp, PlayfulColors.TealBorder)
            ) {
                val blockSuffix = when (event.blocks.size) {
                    1 -> "blok programu"
                    in 2..4 -> "bloki programu"
                    else -> "bloków programu"
                }
                Text(
                    text = "${event.blocks.size} $blockSuffix",
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = PlayfulColors.TealDark,
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
                )
            }
        }
    }
}
